import CustomInput from '../../../reusable/input';
import { DealsNoteObject } from '../../../../params/redux/pipeline';
import { DeleteOutlineRounded } from '@mui/icons-material';
import { notesSchema } from '../../../../schema/notes_schema';
import { addDealNoteThunk, deleteDealNoteThunk } from '../../../../redux/thunks/note';
import { AppDispatch, RootState } from '../../../../redux/store';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { isHTML, timestampToFormattedDate } from '../../../../helper';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: NoteRender Component
 * @paragraph: A React functional component to render and manage notes related to a deal. It allows users to add, view, and delete notes.
 * @interface: NoteInterface
 * @property: {string} notes - The content of the note to be added.
 */
interface NoteInterface {
    notes: string;
}

/**
 * @heading2: NoteRender
 */
const NoteRender: React.FC = () => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     * initialValues: {NoteInterface} - Initial values for adding note.
     * selectedDeal/notes/currentUser: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { notes, loaders } = useSelector((state: RootState) => state.note);
    const { selectedDeal } = useSelector((state: RootState) => state.deal);
    const { currentUser } = useSelector((state: RootState) => state.app);
    const initialValues: NoteInterface = {
        notes: ''
    };

    /**
     * @heading3: Functions
     * @heading4: handleSubmit
     * @paragraph: Handles the submission of the note form.
     * @param: {NoteInterface} values - The form values containing the note content.
     */
    const handleSubmit = async (values: NoteInterface) => {
        try {
            await dispatch(addDealNoteThunk({
                deal_id: selectedDeal?.id as number,
                note: values.notes
            })).unwrap();
        } catch (e) {
            // Catch errors
        }
    };

    /**
    * @heading4: deleteNoteHandler
    * @paragraph: Deletes a specific note by its ID.
    * @param: {number} id - The ID of the note to delete.
    */
    const deleteNoteHandler = async (id: number) => {
        try {
            await dispatch(deleteDealNoteThunk({
                deal_id: selectedDeal?.id as number,
                note_id: id
            })).unwrap();
        } catch (e) {
            // Catch errors
        }
    };

    if (loaders.isLoading) {
        return <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" my={2}>
            <CircularProgress size={24} />
            <Typography ml={2}>Please wait...</Typography>
        </Box>;
    }

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={notesSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                        {/* Input field for note */}
                        <Box width="100%" height="20vh" >
                            <CustomInput
                                type="quill"
                                value={values.notes}
                                name="notes"
                                placeholder="Type content here..."
                                disabled={loaders.isCreating}
                                quillHeight="12vh"
                                error={Boolean(errors.notes && touched.notes)}
                                onChangeForPhone={(value: string) => setFieldValue('notes', value)}
                            />
                        </Box>
                        {/* Action button */}
                        <Button sx={{
                            marginTop: '10px'
                        }} type="submit" variant="outlined" color="primary" fullWidth disabled={loaders.isCreating || loaders.isDeleting}>
                            {loaders.isCreating ? 'Saving...' : 'Save'}
                        </Button>
                    </Form>)}
            </Formik>
            {/* Note List will be render */}
            <Box maxHeight="300px" overflow="auto" mt={1}>
                {
                    notes.map((note: DealsNoteObject, index: number) => (
                        <Box key={index} minHeight="70px" p={1} mt={1} boxSizing="border-box" border="1px solid rgba(231,231,231,1)" borderRadius="5px">
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="rgba(51,56,63,1)" fontSize="14px" fontWeight="600">
                                    Added by {currentUser?.name || `${currentUser?.first_name} ${currentUser?.last_name}`} {' '}
                                    <Typography component="span" fontSize="13px" fontWeight="400">
                                        {timestampToFormattedDate(note.created_at)}
                                    </Typography>
                                </Typography>
                                <IconButton onClick={() => deleteNoteHandler(note.id)}>
                                    <DeleteOutlineRounded fontSize="small" color="error" />
                                </IconButton>
                            </Box>
                            {
                                isHTML(note.description)
                                    ? <Box dangerouslySetInnerHTML={{ __html: note.description }}></Box>
                                    : <Typography variant="body2" color="rgba(111,118,126,1)" fontSize="12px" fontWeight="400">
                                        {note.description}
                                    </Typography>
                            }
                        </Box>
                    ))
                }
            </Box>
        </Box>
    );
};

export default NoteRender;